import React, { useRef, useState, useMemo, Suspense } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { OrbitControls, Environment, useGLTF, useTexture } from "@react-three/drei"
import * as THREE from "three"
import { Box, Typography, Switch, FormControlLabel } from "@mui/material"

// ── Surfboard Model ─────────────────────────────────────────────────────

const MODEL_MAP = {
  'Round': {
    'Diamond': '/static/models/RoundNoseSquareTail.glb?v=4',
    'Square': '/static/models/RoundNoseSquareTail.glb?v=4',
    'Round': '/static/models/RoundNoseSquareTail.glb?v=4',
    'Pin': '/static/models/RoundNoseSquareTail.glb?v=4',
    'Crescent': '/static/models/RoundNoseSquareTail.glb?v=4',
    '_default': '/static/models/RoundNoseSquareTail.glb?v=4',
  },
  'Performance': {
    'Diamond': '/static/models/PerformanceNoseDiamondTail.glb?v=2',
    'Square': '/static/models/PerformanceNoseSquareTail.glb?v=2',
    'Squash': '/static/models/PerformanceNoseSquashTail.glb?v=2',
    'Round': '/static/models/PerformanceNoseRoundTail.glb?v=2',
    '_default': '/static/models/PerformanceNoseSquashTail.glb?v=2',
  },
  '_default': '/static/models/RoundNoseSquareTail.glb?v=4',
}

function getModelUrl(noseShape, tailShape) {
  const noseMap = MODEL_MAP[noseShape] || MODEL_MAP['_default']
  if (typeof noseMap === 'string') return noseMap
  return noseMap[tailShape] || noseMap['_default'] || MODEL_MAP['_default']
}

// ── Patch shape functions ─────────────────────────────────────────────
// Return true if a vertex at normalized position (t=0 nose, t=1 tail)
// and width fraction (wFrac = |w| / maxSliceWidth, 0=center, 1=rail)
// is inside the patch. Derived from the SVG patch paths.

// ── Patch shape functions ─────────────────────────────────────────────
// t: 0=nose, 1=tail. wFrac: 0=center, 1=rail.
// Returns 0-1 intensity (smooth falloff at edges) instead of boolean.

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

const DECK_PATCH_FNS = {
  "None": () => 0,
  "Straight Point": (t, isLB, wFrac) => {
    const start = isLB ? 0.22 : 0.35
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.08
    if (t < start || t > end) return 0
    // Nose-side triangle: width limit goes 0→1 linearly
    if (t < start + tipLen) {
      const widthLimit = (t - start) / tipLen
      return wFrac <= widthLimit ? 1 : 0
    }
    // Tail-side triangle (mirror)
    if (isLB && t > end - tipLen) {
      const widthLimit = (end - t) / tipLen
      return wFrac <= widthLimit ? 1 : 0
    }
    return 1
  },
  "Curved Point": (t, isLB, wFrac) => {
    const start = isLB ? 0.20 : 0.32
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.12
    if (t < start || t > end) return 0
    if (t < start + tipLen) {
      const f = (t - start) / tipLen
      return wFrac <= f * f ? 1 : 0
    }
    if (isLB && t > end - tipLen) {
      const f = (end - t) / tipLen
      return wFrac <= f * f ? 1 : 0
    }
    return 1
  },
  "Round": (t, isLB, wFrac) => {
    const start = isLB ? 0.22 : 0.35
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.10
    if (t < start || t > end) return 0
    if (t < start + tipLen) {
      const f = (t - start) / tipLen
      return wFrac <= Math.sqrt(f) ? 1 : 0
    }
    if (isLB && t > end - tipLen) {
      const f = (end - t) / tipLen
      return wFrac <= Math.sqrt(f) ? 1 : 0
    }
    return 1
  },
}

const TAIL_PATCH_FNS = {
  "None": () => 0,
  "Straight Point": (t, _isLB, wFrac) => {
    const start = 0.70
    const tipLen = 0.09
    if (t < start) return 0
    if (t < start + tipLen) {
      const widthLimit = (t - start) / tipLen
      return wFrac <= widthLimit ? 1 : 0
    }
    return 1
  },
  "Reverse Straight Point": (t, _isLB, wFrac) => {
    const start = 0.69
    const tipLen = 0.09
    if (t < start) return 0
    if (t < start + tipLen) {
      const widthLimit = (t - start) / tipLen
      return wFrac >= (1 - widthLimit) ? 1 : 0
    }
    return 1
  },
  "Curved Point": (t, _isLB, wFrac) => {
    const start = 0.68
    const tipLen = 0.11
    if (t < start) return 0
    if (t < start + tipLen) {
      const f = (t - start) / tipLen
      return wFrac <= f * f ? 1 : 0
    }
    return 1
  },
  "Reverse Curved Point": (t, _isLB, wFrac) => {
    const start = 0.67
    const tipLen = 0.11
    if (t < start) return 0
    if (t < start + tipLen) {
      const f = (t - start) / tipLen
      return wFrac >= (1 - f * f) ? 1 : 0
    }
    return 1
  },
  "Round": (t, _isLB, wFrac) => {
    const start = 0.75
    const tipLen = 0.09
    if (t < start) return 0
    if (t < start + tipLen) {
      const f = (t - start) / tipLen
      return wFrac <= Math.sqrt(f) ? 1 : 0
    }
    return 1
  },
}

function SurfboardModel({ modelUrl, deckColor, bottomColor, stringerColor, isGloss, logoType, deckPatch, tailPatch, finColor, finSetup, stringerCount, onRotation, lengthInches, widthInches, thicknessInches, pinLineColor }) {
  const { scene } = useGLTF(modelUrl)
  const meshRef = useRef()
  const diamondTex = useTexture("/static/models/marlin-logo.png")
  const fishTex = useTexture("/static/models/marlin-fish-logo.png")
  diamondTex.colorSpace = THREE.SRGBColorSpace
  fishTex.colorSpace = THREE.SRGBColorSpace

  const logoTex = logoType === 'fish' ? fishTex : diamondTex

  const data = useMemo(() => {
    const clone = scene.clone()
    const bbox = new THREE.Box3().setFromObject(clone)
    const center = bbox.getCenter(new THREE.Vector3())
    const size = bbox.getSize(new THREE.Vector3())
    clone.position.sub(center)

    const dims = [
      { idx: 0, val: size.x, axis: 'x' },
      { idx: 1, val: size.y, axis: 'y' },
      { idx: 2, val: size.z, axis: 'z' },
    ].sort((a, b) => b.val - a.val)
    const lengthIdx = dims[0].idx
    const widthIdx = dims[1].idx
    const thickIdx = dims[2].idx
    const boardLength = dims[0].val
    const boardWidth = dims[1].val
    const boardThick = dims[2].val
    // Performance models have deck/bottom flipped vs Round nose
    const isPerf = modelUrl.includes('Performance')
    const deckSign = isPerf ? 1 : -1  // deck normal direction

    const deckRaw = new THREE.Color(deckColor)
    const bottomRaw = new THREE.Color(bottomColor)
    const deckIsClear = deckRaw.getHex() === 0xf5f5f5 || deckRaw.getHex() === 0xffffff
    const bottomIsClear = bottomRaw.getHex() === 0xf5f5f5 || bottomRaw.getHex() === 0xffffff
    // Lighten colors for transparent resin tint look
    const white = new THREE.Color(0xffffff)
    const deck = deckRaw
    const bottom = bottomRaw

    // Collect stringer dups to add AFTER traverse (adding during traverse corrupts it)
    const pendingDups = []

    clone.traverse((child) => {
      if (!child.isMesh) return
      const nameLC = (child.name || '').toLowerCase()
      const isStringer = nameLC === 'cube' || nameLC.includes('stringer')
      const isFinBox = !isStringer && !nameLC.includes('surfboard') && (nameLC.includes('fin') || nameLC.includes('cube') || nameLC.includes('box'))

      if (isFinBox) {
        const hasCenterBox = finSetup === "Single" || finSetup === "2+1" || finSetup === "4+1"
        child.visible = hasCenterBox
        if (hasCenterBox) {
          child.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(finColor),
            clearcoat: isGloss ? 0.4 : 0.0,
            roughness: 0.3,
            metalness: 0.0,
            side: THREE.DoubleSide,
          })
        }
        return
      }

      if (isStringer) {
        const sc = stringerCount || 1
        const hasCenter = sc !== 2
        child.visible = hasCenter
        const s = [1, 1, 1]
        s[thickIdx] = 0.95
        child.scale.set(s[0], s[1], s[2])
        child.material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(stringerColor),
          clearcoat: isGloss ? 0.6 : 0.0,
          clearcoatRoughness: isGloss ? 0.1 : 0,
          roughness: 0.45,
          metalness: 0.0,
          side: THREE.DoubleSide,
        })

        if (sc >= 2) {
          const offsets = sc === 2 ? [-0.035, 0.035]
            : sc === 3 ? [-0.30, 0.30]
            : /* 5 */ [-0.16, -0.06, 0.06, 0.16]
          for (const off of offsets) {
            const dup = child.clone()
            dup.geometry = child.geometry.clone()
            dup.name = 'stringer_dup' // prevent re-processing
            dup.visible = true
            const p = [0, 0, 0]
            p[widthIdx] = boardWidth * off
            p[lengthIdx] = boardLength * 0.02
            if (sc === 3) p[thickIdx] = boardThick * 0.12
            dup.position.set(p[0], p[1], p[2])
            const ds = [1, 1, 1]
            ds[thickIdx] = 0.75
            if (sc === 3) ds[lengthIdx] = 0.90
            dup.scale.set(s[0] * ds[0], s[1] * ds[1], s[2] * ds[2])
            pendingDups.push(dup)
          }
        }
        return
      }


      // Deep-clone geometry so we don't corrupt the shared original
      const geo = child.geometry.clone()
      child.geometry = geo
      if (geo.attributes.color) geo.deleteAttribute('color')
      if (!geo.attributes.normal || !geo.attributes.position) return
      const normals = geo.attributes.normal.array
      const positions = geo.attributes.position.array
      const count = normals.length / 3
      const colors = new Float32Array(normals.length)

      child.updateWorldMatrix(true, false)
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(child.matrixWorld)

      const SLICES = 800
      const worldPos = new THREE.Vector3()
      const worldNorm = new THREE.Vector3()
      let minL = Infinity, maxL = -Infinity
      const worldPositions = new Float32Array(count * 3)
      const worldNormals = new Float32Array(count * 3)

      for (let i = 0; i < count; i++) {
        worldPos.set(positions[i*3], positions[i*3+1], positions[i*3+2])
        worldPos.applyMatrix4(child.matrixWorld)
        worldNorm.set(normals[i*3], normals[i*3+1], normals[i*3+2])
        worldNorm.applyMatrix3(normalMatrix).normalize()

        worldPositions[i*3] = worldPos.x - center.x
        worldPositions[i*3+1] = worldPos.y - center.y
        worldPositions[i*3+2] = worldPos.z - center.z
        worldNormals[i*3] = worldNorm.x
        worldNormals[i*3+1] = worldNorm.y
        worldNormals[i*3+2] = worldNorm.z

        const l = worldPositions[i*3 + lengthIdx]
        if (l < minL) minL = l
        if (l > maxL) maxL = l
      }

      const lRange = maxL - minL || 1
      const sliceMaxW = new Float32Array(SLICES).fill(0)

      for (let i = 0; i < count; i++) {
        const l = worldPositions[i*3 + lengthIdx]
        const w = Math.abs(worldPositions[i*3 + widthIdx])
        const sliceIdx = Math.min(Math.floor(((l - minL) / lRange) * SLICES), SLICES - 1)
        if (w > sliceMaxW[sliceIdx]) sliceMaxW[sliceIdx] = w
      }

      for (let i = 0; i < SLICES; i++) {
        if (sliceMaxW[i] === 0) {
          let prev = i - 1, next = i + 1
          while (prev >= 0 && sliceMaxW[prev] === 0) prev--
          while (next < SLICES && sliceMaxW[next] === 0) next++
          const pv = prev >= 0 ? sliceMaxW[prev] : 0
          const nv = next < SLICES ? sliceMaxW[next] : 0
          if (prev >= 0 && next < SLICES) {
            sliceMaxW[i] = pv + (nv - pv) * ((i - prev) / (next - prev))
          } else {
            sliceMaxW[i] = pv || nv
          }
        }
      }

      const globalMaxW = Math.max(...sliceMaxW)
      const overlapDist = globalMaxW * 0.20
      const deckPatchFn = DECK_PATCH_FNS[deckPatch] || DECK_PATCH_FNS["None"]
      const tailPatchFn = TAIL_PATCH_FNS[tailPatch] || TAIL_PATCH_FNS["None"]
      const isLongboard = modelUrl.includes('RoundNose')

      for (let i = 0; i < count; i++) {
        const nThick = worldNormals[i*3 + thickIdx]
        const thickPos = worldPositions[i*3 + thickIdx]
        const w = Math.abs(worldPositions[i*3 + widthIdx])
        const lPos = worldPositions[i*3 + lengthIdx]
        const sliceIdx = Math.min(Math.floor(((lPos - minL) / lRange) * SLICES), SLICES - 1)
        const maxW = sliceMaxW[sliceIdx] || 1
        const inOverlap = (maxW - w) < overlapDist

        // t: 0 = nose, 1 = tail (negative length = tail in model space)
        const t = 1 - ((lPos - minL) / lRange)

        // Use normal for clear deck/bottom, fall back to position when ambiguous
        const isDeckFace = Math.abs(nThick) > 0.15 ? (nThick * deckSign > 0) : (thickPos * deckSign > 0)

        // Smooth the overlap boundary by blending based on distance into overlap
        const overlapFrac = inOverlap ? Math.min(1, (overlapDist - (maxW - w)) / (overlapDist * 0.9)) : 0

        let c
        if (inOverlap) {
          // Same color on both faces to prevent z-fighting flicker
          if (deckIsClear && bottomIsClear) {
            c = deck.clone()
          } else if (deckIsClear) {
            c = bottom.clone()
            c.multiplyScalar(0.6 + (1 - overlapFrac) * 0.2)
          } else if (bottomIsClear) {
            c = deck.clone()
            c.multiplyScalar(0.6 + (1 - overlapFrac) * 0.2)
          } else {
            // Both colored — blend toward the wrapping color
            c = bottom.clone()
            c.lerp(deck, 0.5)
            c.multiplyScalar(0.5 + (1 - overlapFrac) * 0.2)
          }
        } else if (Math.abs(nThick) < 0.15) {
          // Rail — use blended color
          c = (deckIsClear ? bottom : bottomIsClear ? deck : deck.clone().lerp(bottom, 0.5)).clone()
          c.multiplyScalar(0.55)
        } else if (isDeckFace) {
          c = deck.clone()
        } else {
          c = bottom.clone()
        }

        // Apply patch tinting — deck patch on deck only, tail patch on bottom only
        // Sample nearby t/wFrac offsets and average for smoother edges
        {
          const wFrac = maxW > 0 ? w / maxW : 0
          const dt = 0.005, dw = 0.012
          // Check both deck and tail patch — apply whichever is active
          let dSum = 0, tSum = 0
          for (let ot = -1; ot <= 1; ot++) {
            for (let ow = -1; ow <= 1; ow++) {
              const st = t + ot * dt, sw = Math.max(0, wFrac + ow * dw)
              dSum += deckPatchFn(st, isLongboard, sw)
              tSum += tailPatchFn(st, isLongboard, sw)
            }
          }
          dSum /= 9; tSum /= 9
          // Deck patch on deck-facing, tail patch on bottom-facing, both on rails
          const patchSum = isDeckFace ? dSum : (Math.abs(nThick) >= 0.15 ? tSum : Math.max(dSum, tSum))
          if (patchSum > 0.01) {
            c.multiplyScalar(1 - patchSum * 0.35)
          }
        }

        // Pin line — thin stripe on deck face near the rail
        if (pinLineColor && isDeckFace) {
          const wFrac = maxW > 0 ? w / maxW : 0
          if (wFrac > 0.75 && wFrac < 0.80) {
            c.copy(new THREE.Color(pinLineColor))
          }
        }

        colors[i*3] = c.r
        colors[i*3+1] = c.g
        colors[i*3+2] = c.b

      }

      const colorAttr = new THREE.BufferAttribute(colors, 3)
      colorAttr.needsUpdate = true
      geo.setAttribute("color", colorAttr)
      geo.attributes.color.needsUpdate = true
      if (child.material) child.material.dispose()
      child.material = new THREE.MeshPhysicalMaterial({
        vertexColors: true,
        clearcoat: isGloss ? 1.0 : 0.0,
        clearcoatRoughness: isGloss ? 0.05 : 0,
        roughness: isGloss ? 0.12 : 0.4,
        metalness: 0.0,
        reflectivity: isGloss ? 0.5 : 0.2,
        side: THREE.DoubleSide,
      })
    })

    // Add stringer dups after traverse to avoid corrupting the traversal
    for (const dup of pendingDups) clone.add(dup)

    const logoPos = [0, 0, 0]
    logoPos[lengthIdx] = -boardLength * 0.15
    const logoSize = boardWidth * 0.4

    return { clone, thicknessHalf: boardThick / 2, logoPos, logoSize, thickIdx, lengthIdx, widthIdx, boardLength, boardWidth, boardThick, deckSign }
  }, [scene, deckColor, bottomColor, stringerColor, isGloss, finColor, pinLineColor, deckPatch, tailPatch, modelUrl, finSetup, stringerCount])

  useFrame(({ camera }, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.15
      if (onRotation) {
        const azimuth = Math.atan2(camera.position.x, camera.position.z)
        const effectiveY = meshRef.current.rotation.y + azimuth
        const polar = Math.atan2(Math.sqrt(camera.position.x ** 2 + camera.position.z ** 2), camera.position.y)
        onRotation(effectiveY, polar)
      }
    }
  })

  // Dimension scaling: compute scale factors relative to model's natural size
  const dimScale = useMemo(() => {
    if (!data || !lengthInches || !widthInches) return [0.01, 0.01, 0.01]
    // Model dimensions in arbitrary units — convert to inches for ratio
    // Reference: model units / expected inches gives us the conversion
    const refLengthIn = data.boardLength // model's natural length units
    const refWidthIn = data.boardWidth
    const refThickIn = data.boardThick
    // Scale each axis: (target / reference) * base scale
    const base = 0.01
    const ls = lengthInches ? (lengthInches / 110) : 1 // normalize to ~9'2" reference
    const ws = widthInches ? (widthInches / 22) : 1
    const ts = thicknessInches ? (thicknessInches / 2.75) : 1
    const s = [base, base, base]
    s[data.thickIdx] = base * ts
    s[data.lengthIdx] = base * ls
    s[data.widthIdx] = base * ws
    return s
  }, [data, lengthInches, widthInches, thicknessInches])

  return (
    <group ref={meshRef} rotation={[0, Math.PI / 2, 0]}>
      <group scale={dimScale}>
        <primitive object={data.clone} />
        <LogoPlane logoTex={logoTex} data={data} side={1} />
        <LogoPlane logoTex={logoTex} data={data} side={-1} />
        <LeashPlug data={data} finColor={finColor} />
        <FinBoxes data={data} finColor={finColor} finSetup={finSetup} />
      </group>
    </group>
  )
}

// ── Logo Plane ──────────────────────────────────────────────────────────

function LogoPlane({ logoTex, data, side }) {
  const pos = [0, 0, 0]
  pos[data.lengthIdx] = data.logoPos[data.lengthIdx]
  if (side > 0) {
    pos[0] = data.thicknessHalf * 0.94
  } else {
    pos[0] = -data.thicknessHalf * 0.15
  }

  const euler = new THREE.Euler()
  if (data.thickIdx === 0) {
    euler.set(0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0)
  } else if (data.thickIdx === 1) {
    euler.set(side > 0 ? -Math.PI / 2 : Math.PI / 2, 0, 0)
  } else {
    euler.set(side < 0 ? Math.PI : 0, 0, 0)
  }

  return (
    <mesh position={pos} rotation={euler}>
      <planeGeometry args={[data.logoSize, data.logoSize]} />
      <meshBasicMaterial map={logoTex} transparent alphaTest={0.1} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  )
}

// ── Leash Plug ─────────────────────────────────────────────────────────

function LeashPlug({ data, finColor }) {
  const pos = [0, 0, 0]
  pos[0] = -data.thicknessHalf * 0.6
  pos[data.lengthIdx] = -data.boardLength * 0.44

  const euler = new THREE.Euler()
  if (data.thickIdx === 0) euler.set(0, -Math.PI / 2, 0)

  const radius = data.boardWidth * 0.02

  return (
    <mesh position={pos} rotation={euler}>
      <circleGeometry args={[radius, 24]} />
      <meshStandardMaterial color={finColor || 0x333333} roughness={0.8} side={THREE.FrontSide} />
    </mesh>
  )
}

// ── Fin Boxes ──────────────────────────────────────────────────────────

// Positions normalized from SVG: t along length (0=nose, 1=tail), w as fraction of half-width
// SVG board: y 18(nose)-937(tail)=919 range, center x=165, half-width≈120
const FIN_BOX_DEFS = {
  centerLong:  { t: 0.88, len: 0.12, w: 0 },       // center, long box
  centerShort: { t: 0.95, len: 0.05, w: 0, close: true },  // center, short box - sits closer
  left:        { t: 0.84, len: 0.05, w: -0.58 },   // left side
  right:       { t: 0.84, len: 0.05, w: 0.58 },    // right side
  rearLeft:    { t: 0.91, len: 0.05, w: -0.35 },   // rear left
  rearRight:   { t: 0.91, len: 0.05, w: 0.35 },    // rear right
}

// Center box is baked into GLB for Single/2+1/4+1 — render short center + sides for others
const FIN_SETUP_MAP = {
  "Single":    [],
  "2+1":       ["left", "right"],
  "Twin":      ["left", "right"],
  "Thruster":  ["centerShort", "left", "right"],
  "Quad":      ["left", "right", "rearLeft", "rearRight"],
  "4+1":       ["left", "right", "rearLeft", "rearRight"],
  "5-Fin":     ["centerShort", "left", "right", "rearLeft", "rearRight"],
}

function FinBox({ data, finColor, def }) {
  const boxWidth = data.boardWidth * 0.03
  const boxLen = data.boardLength * def.len
  const boxThick = data.boardThick * 0.08

  const pos = [0, 0, 0]
  pos[data.thickIdx] = data.thicknessHalf * (def.close ? -0.3 : def.t > 0.88 ? -0.15 : 0.20)
  pos[data.lengthIdx] = -data.boardLength * (def.t - 0.5)
  pos[data.widthIdx] = data.boardWidth * 0.5 * def.w

  const size = [0, 0, 0]
  size[data.thickIdx] = boxThick
  size[data.lengthIdx] = boxLen
  size[data.widthIdx] = boxWidth

  // Tilt to match board curvature
  const rot = [0, 0, 0]
  // Rocker tilt along length axis (tail kicks up)
  rot[data.widthIdx] = def.close ? -0.18 : (def.t > 0.88 ? -0.12 : -0.08)
  // Cross-section tilt for side fins
  if (def.w !== 0) {
    rot[data.lengthIdx] = def.w > 0 ? -0.15 : 0.15
  }

  return (
    <mesh position={pos} rotation={rot}>
      <boxGeometry args={size} />
      <meshPhysicalMaterial color={finColor || 0x333333} roughness={0.3} metalness={0} side={THREE.DoubleSide} />
    </mesh>
  )
}

function FinBoxes({ data, finColor, finSetup }) {
  const boxes = FIN_SETUP_MAP[finSetup] || FIN_SETUP_MAP["Thruster"]
  return (
    <group>
      {boxes.map((key) => (
        <FinBox key={key} data={data} finColor={finColor} def={FIN_BOX_DEFS[key]} />
      ))}
    </group>
  )
}

// ── Board Glow ──────────────────────────────────────────────────────────

function AnimatedLight({ position, color, baseIntensity, distance, speed }) {
  const ref = useRef()
  const offset = useRef(Math.random() * Math.PI * 2)
  useFrame(({ clock }) => {
    if (ref.current) {
      const t = clock.elapsedTime * speed + offset.current
      ref.current.intensity = baseIntensity * (0.6 + Math.sin(t) * 0.4)
    }
  })
  return <pointLight ref={ref} position={position} color={color} intensity={baseIntensity} distance={distance} />
}

function BoardGlow({ deckColor, bottomColor }) {
  const glowColor = useMemo(() => new THREE.Color(deckColor).lerp(new THREE.Color(bottomColor), 0.5), [deckColor, bottomColor])

  return (
    <>
      <AnimatedLight position={[0, 0, -2]} color={glowColor} baseIntensity={0.5} distance={8} speed={0.3} />
      <AnimatedLight position={[0, 0.5, -1.5]} color={glowColor} baseIntensity={0.3} distance={6} speed={0.5} />
      <AnimatedLight position={[0, -0.5, -1.5]} color={glowColor} baseIntensity={0.3} distance={6} speed={0.7} />
      <AnimatedLight position={[1, 0, -1.2]} color={glowColor} baseIntensity={0.2} distance={5} speed={0.4} />
      <AnimatedLight position={[-1, 0, -1.2]} color={glowColor} baseIntensity={0.2} distance={5} speed={0.6} />
    </>
  )
}

// ── UI Components ───────────────────────────────────────────────────────

const BOARD_COLORS = [
  { name: "Clear", hex: "#F5F5F5" },
  { name: "Red", hex: "#ff0000" },
  { name: "Strawberry", hex: "#ff6e6e" },
  { name: "Hot Pink", hex: "#ff035f" },
  { name: "Orange", hex: "#ff8800" },
  { name: "Yellow", hex: "#fff200" },
  { name: "Seafoam", hex: "#00ffae" },
  { name: "Sky Blue", hex: "#00b7ff" },
  { name: "Blue", hex: "#005eff" },
  { name: "Sage", hex: "#a2cf76" },
  { name: "Purple", hex: "#cc00ff" },
  { name: "Lavender", hex: "#f4baff" },
]

const STRINGER_COLORS = [
  { name: "Basswood", hex: "#fff9c7" },
  { name: "Darkwood", hex: "#0a0806" },
  { name: "Redwood", hex: "#a64602" },
]

const COLOR_MODES = [
  { label: "Deck Only", deck: true, bottom: false },
  { label: "Bottom Only", deck: false, bottom: true },
  { label: "Both Sides", deck: true, bottom: true },
]

const LOGO_OPTIONS = [
  { label: "Diamond", value: "diamond" },
  { label: "Fish", value: "fish" },
]

const DECK_PATCHES = ["None", "Straight Point", "Curved Point", "Round"]
const TAIL_PATCHES = ["None", "Straight Point", "Reverse Straight", "Curved Point", "Reverse Curved", "Round"]

function SwatchRow({ label, colors, selected, onSelect }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", width: 55, textAlign: "right", textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </Typography>
      {colors.map((c) => (
        <Box
          key={c.hex} onClick={() => onSelect(c.hex)} title={c.name}
          sx={{
            width: 26, height: 26, borderRadius: "50%", bgcolor: c.hex, cursor: "pointer",
            border: selected === c.hex ? "3px solid #fff" : "2px solid rgba(255,255,255,0.2)",
            transition: "border 0.15s", "&:hover": { border: "3px solid #fff" },
          }}
        />
      ))}
    </Box>
  )
}

function OptionRow({ label, options, selected, onSelect }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", width: 55, textAlign: "right", textTransform: "uppercase", letterSpacing: 1 }}>
        {label}
      </Typography>
      {options.map((opt) => {
        const val = typeof opt === 'string' ? opt : opt.value
        const lbl = typeof opt === 'string' ? opt : opt.label
        return (
          <Box key={val} onClick={() => onSelect(val)}
            sx={{ px: 1.2, py: 0.3, borderRadius: 1, cursor: "pointer", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: 0.5,
              color: selected === val ? "#fff" : "rgba(255,255,255,0.4)",
              bgcolor: selected === val ? "rgba(255,255,255,0.15)" : "transparent",
              border: selected === val ? "1px solid rgba(255,255,255,0.3)" : "1px solid transparent",
              "&:hover": { color: "#fff" },
            }}
          >{lbl}</Box>
        )
      })}
    </Box>
  )
}

// ── Main Viewer ─────────────────────────────────────────────────────────

class ThreeErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  render() {
    if (this.state.error) return (
      <Box sx={{ color: '#fff', p: 4, fontFamily: "'Josefin Sans', sans-serif" }}>
        <Typography variant="h6">3D Viewer Error</Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>{this.state.error.message}</Typography>
      </Box>
    )
    return this.props.children
  }
}

export default function BoardViewer3D() {
  const [boardColor, setBoardColor] = useState("#ff0000")
  const [colorMode, setColorMode] = useState(2)
  const [stringerColor, setStringerColor] = useState("#fff9c7")
  const [isGloss, setIsGloss] = useState(true)
  const [logoType, setLogoType] = useState("diamond")
  const [deckPatch, setDeckPatch] = useState("None")
  const [tailPatch, setTailPatch] = useState("None")
  const [finColor, setFinColor] = useState("#000000")
  const [finSetup, setFinSetup] = useState("Thruster")
  const [stringerCount, setStringerCount] = useState(1)

  const CLEAR = "#F5F5F5"
  const mode = COLOR_MODES[colorMode]
  const deckColor = mode.deck ? boardColor : CLEAR
  const bottomColor = mode.bottom ? boardColor : CLEAR

  return (
    <Box sx={{ width: "100vw", height: "100vh", bgcolor: "#111", position: "relative" }}>
      <ThreeErrorBoundary>
      <Canvas
        camera={{ position: [0, 0.5, 5.5], fov: 35 }}
        gl={{ antialias: true, toneMapping: THREE.LinearToneMapping }}
        dpr={[1, 2]}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={0.5} />
        <directionalLight position={[-5, -5, -5]} intensity={0.5} />

        <Suspense fallback={null}>
          <SurfboardModel
            modelUrl="/static/models/RoundNoseSquareTail.glb?v=4"
            deckColor={deckColor} bottomColor={bottomColor}
            stringerColor={stringerColor} isGloss={isGloss}
            logoType={logoType} deckPatch={deckPatch} tailPatch={tailPatch} finColor={finColor} finSetup={finSetup} stringerCount={stringerCount}
          />
          <Environment preset="studio" environmentIntensity={0.4} />
        </Suspense>

        <OrbitControls enablePan enableZoom minDistance={0.3} maxDistance={20} minPolarAngle={0} maxPolarAngle={Math.PI} />
      </Canvas>
      </ThreeErrorBoundary>

      <Box sx={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", gap: 0.8, bgcolor: "rgba(0,0,0,0.5)", backdropFilter: "blur(10px)", borderRadius: 2, p: 2 }}>
        <SwatchRow label="Color" colors={BOARD_COLORS} selected={boardColor} onSelect={setBoardColor} />
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          {COLOR_MODES.map((m, i) => (
            <Box key={m.label} onClick={() => setColorMode(i)}
              sx={{ px: 1.5, py: 0.4, borderRadius: 1, cursor: "pointer", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 1,
                color: colorMode === i ? "#fff" : "rgba(255,255,255,0.4)",
                bgcolor: colorMode === i ? "rgba(255,255,255,0.15)" : "transparent",
                border: colorMode === i ? "1px solid rgba(255,255,255,0.3)" : "1px solid transparent",
                "&:hover": { color: "#fff" },
              }}
            >{m.label}</Box>
          ))}
        </Box>
        <SwatchRow label="Stringer" colors={STRINGER_COLORS} selected={stringerColor} onSelect={setStringerColor} />
        <OptionRow label="Logo" options={LOGO_OPTIONS} selected={logoType} onSelect={setLogoType} />
        <OptionRow label="Deck" options={DECK_PATCHES} selected={deckPatch} onSelect={setDeckPatch} />
        <OptionRow label="Tail" options={TAIL_PATCHES} selected={tailPatch} onSelect={setTailPatch} />
        <OptionRow label="Fins" options={[{label: "Black", value: "#000000"}, {label: "White", value: "#ffffff"}]} selected={finColor} onSelect={setFinColor} />
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <FormControlLabel
            control={<Switch checked={isGloss} onChange={(e) => setIsGloss(e.target.checked)} size="small" />}
            label={<Typography sx={{ color: "#fff", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1 }}>{isGloss ? "Gloss" : "Matte"}</Typography>}
          />
        </Box>
      </Box>

      <Typography variant="h6" sx={{ position: "absolute", top: 24, left: 24, color: "#fff", fontWeight: 300, letterSpacing: 2, textTransform: "uppercase", fontSize: "0.85rem" }}>
        Marlin Surfboards
      </Typography>
    </Box>
  )
}

// ── Embeddable 3D Viewer (no bg, no controls UI) ───────────────────────

export function BoardViewer3DEmbed({ deckColor = "#F5F5F5", bottomColor = "#F5F5F5", stringerColor = "#fff9c7", isGloss = true, logoType = "diamond", finColor = "#000000", onRotation, noseShape = "Round", tailShape = "Square", lengthInches, widthInches, thicknessInches, pinLineColor, deckPatch = "None", tailPatch = "None", finSetup = "Thruster", stringerCount = 1 }) {
  const modelUrl = getModelUrl(noseShape, tailShape)
  return (
    <ThreeErrorBoundary>
    <Canvas
      camera={{ position: [0, 0.3, 6.5], fov: 35, near: 0.1 }}
      gl={{ antialias: true, toneMapping: THREE.LinearToneMapping, alpha: true }}
      dpr={[1, 2]}
      style={{ background: "transparent", width: "100%", height: "100%" }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.5} />
      <directionalLight position={[-5, -5, -5]} intensity={0.5} />

      <Suspense fallback={null}>
        <SurfboardModel
          modelUrl={modelUrl}
          deckColor={deckColor} bottomColor={bottomColor}
          stringerColor={stringerColor} isGloss={isGloss}
          logoType={logoType} deckPatch={deckPatch} tailPatch={tailPatch} finColor={finColor} finSetup={finSetup} stringerCount={stringerCount}
          onRotation={onRotation}
          lengthInches={lengthInches} widthInches={widthInches} thicknessInches={thicknessInches}
          pinLineColor={pinLineColor}
        />
        <Environment preset="studio" environmentIntensity={0.2} />
      </Suspense>

      <OrbitControls enablePan={false} enableZoom={false} minPolarAngle={0} maxPolarAngle={Math.PI} />
    </Canvas>
    </ThreeErrorBoundary>
  )
}
